"use client";

import clsx from "clsx";
import { Monogram } from "@/components/persona/Monogram";
import type { EventLogState } from "@/lib/sse/useSSE";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * M4 — Aurora's counterpart for T1 1.6 (proposal popup) + T1 1.8
 * (proposal accepted + handshake #2).
 *
 * The audience-facing default at 1.6 has Aurora rendered as a passive
 * "counterparty driving" placeholder. This component gives her agency:
 * her trade desk has just received Al Madar's proposal. She can read
 * the proposal contents, see the buyer's verification status, the
 * jurisdiction-pair classification, and the recommended next step
 * (accept / counter / decline). The accept path is presenter-driven
 * (the platform fires `trade.ProposalAccepted` automatically a few
 * screens later); this scene is the moment Aurora SEES it.
 *
 * Three-state render based on which projection events have arrived:
 *
 *   • Before `trade.ProposalSubmitted` — "Awaiting proposal" header,
 *     buyer-onboarding signals feed.
 *   • After  `trade.ProposalSubmitted` — full proposal card visible,
 *     "Inbound proposal · ready for review" header, recommended action.
 *   • After  `trade.ProposalAccepted`  — "Accepted ✓ · contract drafted"
 *     header with the contract ref, plus the buyer's freshly-issued
 *     OrbID inline.
 */
export function InboundProposalTriage({ ev, className }: { ev: EventLogState; className?: string }) {
  const t   = useTranslations("inboundProposalTriage");
  const evs = ev.events;
  const proposalSubmitted = evs.some((e) => e.eventType === "trade.ProposalSubmitted");
  const proposalAccepted  = evs.some((e) => e.eventType === "trade.ProposalAccepted");
  const orbIdIssued       = evs.some((e) => e.eventType === "identity.OrbIDIssued");

  // Aurora's trust signal — surface the buyer's KYB summary so the
  // audience reads what she sees BEFORE accepting.
  const verifiedTraderTier = orbIdIssued ? t("verifiedTraderEstablished") : t("verifiedTraderPending");

  return (
    <div className={clsx("flex h-full flex-col gap-4", className)}>
      <div className="glass p-5">
        <div className="flex items-center gap-4">
          <Monogram name="Aurora Aves Brasil" flag="BR" size={36} accent="var(--state-verified)" />
          <span className="font-mono text-[10px] text-orbitra-muted">→</span>
          <Monogram name="Al Madar Foods" flag="AE" size={36} accent="var(--state-cold)" />
          <div className="flex-1">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
              {t("kickerYourView")}
            </p>
            <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">
              {proposalAccepted
                ? t("headingAccepted")
                : proposalSubmitted
                  ? t("headingInbound")
                  : t("headingAwaiting")}
            </h3>
            <p className="mt-1 text-[12px] text-orbitra-teal-soft">
              {proposalSubmitted ? t("bodyProposalSubmitted") : t("bodyWatching")}
            </p>
          </div>
          <span className={clsx(
            "rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-wider",
            proposalAccepted
              ? "border-policy-pass/40 bg-policy-pass/10 text-policy-pass"
              : proposalSubmitted
                ? "border-state-financed/40 bg-state-financed/10 text-state-financed"
                : "border-state-traced/40 bg-state-traced/10 text-state-traced",
          )}>
            {proposalAccepted ? t("badgeAccepted") : proposalSubmitted ? t("badgeReview") : t("badgeWatching")}
          </span>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-3 gap-4">
        <div className="glass col-span-2 flex flex-col p-5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
            {t("proposalKicker")}
          </p>
          {!proposalSubmitted ? (
            <p className="mt-3 text-[12px] text-orbitra-muted italic">
              Proposal will appear here once Al Madar submits. He's currently in
              <span className="mx-1 font-mono text-state-verified">{t("onboardingChip")}</span>
              — Orbitra is handling the issuance flow on his side.
            </p>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
              <Field label={t("fieldBuyer")}               value="Al Madar Foods LLC · UAE" />
              <Field label={t("fieldOrbId")}               value={orbIdIssued ? "corbid:5493-almadar-ae" : "TMP-ALMADAR-2026-0742"} mono />
              <Field label={t("fieldVerificationTier")}   value={verifiedTraderTier} />
              <Field label={t("fieldCommodity")}           value="Frozen Halal Whole Chicken · HS 0207.12" />
              <Field label={t("fieldQuantity")}            value="28,000 kg" />
              <Field label={t("fieldTargetPrice")}        value="USD 850,000 (CIF Jebel Ali)" />
              <Field label={t("fieldIncoterms")}           value="CIF" />
              <Field label={t("fieldColdChainSpec")}     value="-18 °C IQF · monitored" />
              <Field label={t("fieldSettlementCurrency")} value="USDC primary · USDT secondary" />
              <Field label={t("fieldFunding")}             value={t("fieldFundingValue")} />
            </div>
          )}

          <div className={clsx(
            "mt-auto flex items-center gap-3 rounded-md border px-3 py-2 text-[11px]",
            proposalAccepted
              ? "border-policy-pass/30 bg-policy-pass/8 text-orbitra-paper"
              : proposalSubmitted
                ? "border-state-financed/30 bg-state-financed/8 text-state-financed"
                : "border-orbitra-muted/30 bg-orbitra-ink/40 text-orbitra-muted",
          )}>
            <span className="font-mono text-[9px] font-bold uppercase tracking-[0.18em]">{t("echoAgentLabel")}</span>
            <span>
              {proposalAccepted
                ? t("echoAccepted")
                : proposalSubmitted
                  ? t("echoSubmitted")
                  : t("echoWaiting")}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="glass p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
              {t("pipelineKicker")}
            </p>
            <ul className="mt-2 flex flex-col gap-1.5 text-[11px]">
              <PipelineRow label={t("pipelineActiveTrades")}     value="0" />
              <PipelineRow label={t("pipelineInboundProposals")} value={proposalSubmitted ? "1 · Al Madar" : "—"} highlight={proposalSubmitted && !proposalAccepted} />
              <PipelineRow label={t("pipelineDraftedContracts")} value={proposalAccepted ? "1 · ORB-CTR-2026-0042" : "—"} highlight={proposalAccepted} />
              <PipelineRow label={t("pipelineClosed")} value="25 · USD 15.65M" />
            </ul>
          </div>

          <div className="glass p-4 text-[11px]">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("whyMattersKicker")}
            </p>
            <p className="mt-2 text-orbitra-paper">
              {t("whyMattersBody")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <p className={clsx("mt-0.5 text-orbitra-paper", mono && "font-mono text-[11px]")}>{value}</p>
    </div>
  );
}

function PipelineRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <li className={clsx(
      "flex items-center justify-between rounded border px-2 py-1",
      highlight
        ? "border-state-financed/40 bg-state-financed/8 text-state-financed"
        : "border-orbitra-muted/20 bg-orbitra-ink/30 text-orbitra-muted",
    )}>
      <span className="font-mono text-[9px] uppercase tracking-[0.18em]">{label}</span>
      <span className={clsx("text-orbitra-paper", highlight && "font-semibold")}>{value}</span>
    </li>
  );
}
